<template>
    <div>
        <b-card no-body>
            <div class="m-2">
                <b-row>
                    <b-col cols="12" md="6" sm="12">
                        <h1>{{ i18nT('Organisation Chart') }}</h1>
                    </b-col>
                    <b-col cols="12" md="6" sm="12">
                        <b-dropdown
                            v-if="iCan('employees', 'write')"
                            id="add-new-dropdown"
                            variant="primary"
                            class="float-right"
                            right
                        >
                            <template #button-content>
                                <feather-icon icon="PlusCircleIcon" />
                                {{ i18nT('Add employee') }}
                            </template>
                            <b-dropdown-item>
                                <b-link :to="{ name: 'create-employee' }">
                                    <font-awesome-icon class="mr-25 ml-25" :icon="['fas', 'user-plus']" />
                                    {{ i18nT('Add') }}
                                </b-link>
                            </b-dropdown-item>
                            <b-dropdown-item v-b-modal.modal-import-csv>
                                <div class="text-primary">
                                    <font-awesome-icon class="mr-25" :icon="['fas', 'file-import']" />
                                    {{ i18nT('Import') }}
                                </div>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" md="12">
                        <div>
                            <!-- OrgChart JS renders into this div -->
                            <div id="tree" style="width: 100%; height: 800px;"></div>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </b-card>
    </div>
</template>

<script>
// Note: adjust the import path as needed for your project setup
import OrgChart from '@/libs/balkanapp/orgchart.js'
import {
    BCard,
    BRow,
    BCol,
    BDropdown,
    BDropdownItem,
    BLink,
    VBModal,
    VBTooltip
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import useAuth from '@/auth/useAuth'
import { dictToSelectArray } from '@core/utils/utils'

export default {
    name: 'OrganisationStructure',
    components: {
        BCard,
        BRow,
        BCol,
        BDropdown,
        BDropdownItem,
        BLink
    },
    directives: {
        Ripple,
        'b-modal': VBModal,
        'b-tooltip': VBTooltip
    },
    data() {
        return {
            userId: useAuth.getProperUserId(),
            groups: [],
            departments: [],
            allEmployees: [],
            chart: null
        }
    },
    methods: {
      setupChart(){
              // --- Define custom templates ---
              // Extend the "ana" template to include a custom plus button
              OrgChart.templates.ana.plus =
                  `<circle cx="15" cy="15" r="15" fill="#ffffff" stroke="#aeaeae" stroke-width="1"></circle>
       <text text-anchor="middle" style="font-size: 16px;cursor:pointer;" fill="#757575" x="15" y="22">{collapsed-children-count}</text>`;

              OrgChart.templates.ana.node = `
          <defs>
            <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="5" result="blur"/>
              <feOffset in="blur" dx="3" dy="3" result="offsetBlur"/>
              <feComponentTransfer in="offsetBlur" result="shadowAlpha">
                <feFuncA type="linear" slope="0.3" intercept="0"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode in="shadowAlpha"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            </defs>
          <rect x="0" y="0" width="{w}" height="{h}" rx="5" ry="5" fill="#5a5aff" filter="url(#dropShadow)"/>
        `;

              // Create a new template based on "ana" for IT team members
              OrgChart.templates.itTemplate = Object.assign({}, OrgChart.templates.ana);
              OrgChart.templates.itTemplate.nodeMenuButton = "";
              OrgChart.templates.itTemplate.nodeCircleMenuButton = {
                  radius: 18,
                  x: 250,
                  y: 60,
                  color: '#fff',
                  stroke: '#aeaeae'
              };

              // Customize padding for invisible groups
              OrgChart.templates.invisibleGroup.padding = [20, 0, 0, 0];

              // --- Define helper functions ---
              function getOptions() {
                  const searchParams = new URLSearchParams(window.location.search);
                  let fit = searchParams.get('fit');
                  let scaleInitial = 1;
                  if (fit === 'yes') {
                      scaleInitial = OrgChart.match.boundary;
                  }
                  return {scaleInitial};
              }

              const options = getOptions();

              // --- Create OrgChart instance ---
              const container = document.getElementById("tree");
              const chart = new OrgChart(container, {
                  mouseScrool: OrgChart.action.scroll,
                  scaleInitial: options.scaleInitial,
                  enableSearch: false,
                  template: "ana",
                  enableDragDrop: true,
                  assistantSeparation: 170,
                  nodeCircleMenu: {
                      details: {
                          icon: OrgChart.icon.details(24, 24, '#aeaeae'),
                          text: "Details",
                          color: "white"
                      },
                      edit: {
                          icon: OrgChart.icon.edit(24, 24, '#aeaeae'),
                          text: "Edit node",
                          color: "white"
                      },
                      add: {
                          icon: OrgChart.icon.add(24, 24, '#aeaeae'),
                          text: "Add node",
                          color: "white"
                      },
                      remove: {
                          icon: OrgChart.icon.remove(24, 24, '#aeaeae'),
                          text: "Remove node",
                          color: '#fff'
                      },
                      addLink: {
                          icon: OrgChart.icon.link(24, 24, '#aeaeae'),
                          text: "Add C link(drag and drop)",
                          color: '#fff',
                          draggable: true
                      }
                  },
                  menu: {
                      pdfPreview: {
                          text: "Export to PDF",
                          icon: OrgChart.icon.pdf(24, 24, '#7A7A7A'),
                          onClick: preview
                      },
                      csv: {text: "Save as CSV"}
                  },
                  nodeMenu: {
                      details: {text: "Details"},
                      edit: {text: "Edit"},
                      add: {text: "Add"},
                      remove: {text: "Remove"}
                  },
                  align: OrgChart.ORIENTATION,
                  toolbar: {
                      fullScreen: true,
                      zoom: true,
                      fit: true,
                      expandAll: true
                  },
                  nodeBinding: {
                      field_0: "name",
                      field_1: "title",
                      img_0: "img"
                  },
                  tags: {
                      "top-management": {
                          template: "invisibleGroup",
                          subTreeConfig: {
                              orientation: OrgChart.orientation.bottom,
                              collapse: {
                                  level: 1
                              }
                          }
                      },
                      "it-team": {
                          subTreeConfig: {
                              layout: OrgChart.mixed,
                              collapse: {
                                  level: 1
                              }
                          }
                      },
                      "hr-team": {
                          subTreeConfig: {
                              layout: OrgChart.treeRightOffset,
                              collapse: {
                                  level: 1
                              }
                          }
                      },
                      "sales-team": {
                          subTreeConfig: {
                              layout: OrgChart.treeLeftOffset,
                              collapse: {
                                  level: 1
                              }
                          }
                      },

                      "menu-without-add": {
                          nodeMenu: {
                              details: {text: "Details"},
                              edit: {text: "Edit"},
                              remove: {text: "Remove"}
                          }
                      },
                      "department": {
                          template: "group",
                          nodeMenu: {
                              addManager: {
                                  text: "Add new manager",
                                  icon: OrgChart.icon.add(24, 24, "#7A7A7A"),
                                  onClick: addManager
                              },
                              remove: {text: "Remove department"},
                              edit: {text: "Edit department"},
                              nodePdfPreview: {
                                  text: "Export department to PDF",
                                  icon: OrgChart.icon.pdf(24, 24, "#7A7A7A"),
                                  onClick: nodePdfPreview
                              }
                          }
                      },
                      "it-team-member": {
                          template: "itTemplate"
                      }
                  },
                  clinks: [
                      {from: 11, to: 18}
                  ]
              });

              // Save chart instance to component data if needed later
              this.chart = chart;

              // --- Define event handlers and helper functions ---
              function preview() {
                  OrgChart.pdfPrevUI.show(chart, {
                      format: 'A4'
                  });
              }

              function nodePdfPreview(nodeId) {
                  OrgChart.pdfPrevUI.show(chart, {
                      format: 'A4',
                      nodeId: nodeId
                  });
              }

              function addSharholder(nodeId) {
                  chart.addNode({id: OrgChart.randomId(), pid: nodeId, tags: ["menu-without-add"]});
              }


              function addManager(nodeId) {
                  chart.addNode({id: OrgChart.randomId(), stpid: nodeId});
              }

              // Set up nodeCircleMenu UI event listeners
              chart.nodeCircleMenuUI.on('click', function (sender, args) {
                  switch (args.menuItem.text) {
                      case "Details":
                          chart.editUI.show(args.nodeId, true);
                          break;
                  }
              });

              chart.nodeCircleMenuUI.on('drop', function (sender, args) {
                  chart.addClink(args.from, args.to).draw(OrgChart.action.update);
              });

              chart.on("added", function (sender, id) {
                  sender.editUI.show(id);
              });

              chart.on('drop', function (sender, draggedNodeId, droppedNodeId) {
                  let draggedNode = sender.getNode(draggedNodeId);
                  let droppedNode = sender.getNode(droppedNodeId);
                  if (droppedNode !== undefined) {
                      if (droppedNode.tags.indexOf("department") !== -1 && draggedNode.tags.indexOf("department") === -1) {
                          let draggedNodeData = sender.get(draggedNode.id);
                          draggedNodeData.pid = null;
                          draggedNodeData.stpid = droppedNode.id;
                          sender.updateNode(draggedNodeData);
                          return false;
                      }
                  }
              });

              chart.on('exportstart', function (sender, args) {
                  // Assuming you have an element with id "myStyles" in your page
                  args.styles = document.getElementById('myStyles')?.outerHTML || "";
              });

              // --- Load the node data ---
              chart.load([
                  {id: "top-management", tags: ["top-management"]},
                  ...this.departments,
                  ...this.allEmployees
              ]);

      },
    },
    created() {
        this.$http.get(`employees/employeeGroups`).then(({data}) => {
            console.log("data: ", data)
            this.groups.push(
                ...data.data.employee_groups.map(group => ({
                    value: group.group.Id,
                    text: group.group.Label
                }))
            )
        })

        this.$http.get(`system/departments`).then(({data}) => {

            let listLike = dictToSelectArray(data.data);




            this.$http.get(`employees/employeestreeaslist`).then(({data}) => {
                console.log("All employee records:", data.data.employees);
                this.allEmployees = data.data.employees;

                // Filter departments to keep only those with at least one employee
                const filteredDepartments = listLike.filter(department =>
                    this.allEmployees.some(emp => emp.stpid == department.value)
                );

                this.departments = filteredDepartments.map(department => ({
                    id: department.value,
                    pid: "top-management",
                    name: department.text
                }));

                console.log("Departments: ", filteredDepartments);

                this.setupChart();
            })

        })



        console.log("allEmployees after mapping: ", this.allEmployees);

    },
}
</script>

<style lang="scss">
/* Add any custom styles here */
</style>
